// Property — GM-TXT-03 D3 (Oct 5, 2026). House stories for ops: look up a house, set its stories, and decide Maestros'
// "PISOS" reports and Property Owner answers that disagree with the county. English (owner, Oct 5); Spanish only where
// it quotes what a Maestro reads. Every change goes through the change pipeline (preview → reason → apply → change log).
// Precedence: ops > Property Owner > accepted Maestro report > county records. Jobs keep their own copy; prices never change.
import { useCallback, useEffect, useState } from "react";
import { canApply, ops, STORIES_SOURCE_LABEL, type PropertyLookup, type StoriesDisagreement, type StoriesReport } from "../lib/ops";
import { useOperator } from "../lib/context";
import { ChangePanel, type Pending } from "../components/ChangePanel";
import { Chip, Loading, Notice, PageFrame, SectionLabel } from "../components/ui";

const CHOICES = [1, 1.5, 2, 2.5, 3, 4];
const floors = (n: number | null | undefined) => (n && n > 0 ? `${n} (texts: “Casa de ${Math.ceil(n)} ${Math.ceil(n) === 1 ? "piso" : "pisos"}”)` : "Unknown");
const when = (iso: string | null | undefined) => (iso ? new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "—");

export default function Property() {
  const { role } = useOperator();
  const allowed = canApply(role);
  const [pending, setPending] = useState<Pending | null>(null);
  const [reports, setReports] = useState<StoriesReport[] | null>(null);
  const [disagree, setDisagree] = useState<StoriesDisagreement[] | null>(null);
  const [error, setError] = useState("");
  const [street, setStreet] = useState("");
  const [zip, setZip] = useState("");
  const [found, setFound] = useState<PropertyLookup | null>(null);
  const [looking, setLooking] = useState(false);
  const [pick, setPick] = useState<number | null>(null);
  const [rejecting, setRejecting] = useState<string | null>(null);
  const [rejectWhy, setRejectWhy] = useState("");

  const refresh = useCallback(async () => {
    try {
      const [r, d] = await Promise.all([ops.storiesReports(), ops.storiesDisagreements()]);
      setReports(r); setDisagree(d); setError("");
    } catch (e) { setError((e as Error).message); }
  }, []);
  useEffect(() => { void refresh(); }, [refresh]);

  async function lookup() {
    setLooking(true); setFound(null); setPick(null);
    try { setFound(await ops.propertyLookup(street.trim(), zip.trim())); } catch (e) { setError((e as Error).message); }
    finally { setLooking(false); }
  }
  async function afterApply() {
    await refresh();
    if (found?.found && street && zip) setFound(await ops.propertyLookup(street.trim(), zip.trim()));
  }
  async function reject(id: string) {
    try {
      const r = await ops.rejectStoriesReport(id, rejectWhy.trim());
      if (!r.ok) setError("That report was already decided.");
      setRejecting(null); setRejectWhy(""); await refresh();
    } catch (e) { setError((e as Error).message); }
  }

  const p = found?.property;
  const panel = pending ? <ChangePanel pending={pending} onClose={() => setPending(null)} onApplied={afterApply} /> : null;
  return (
    <PageFrame title="Property" lede="House stories: what Maestros read as “Casa de N pisos”. Ops > Property Owner > Maestro report > county records." panel={panel}>
      {error && <Notice kind="error">{error}</Notice>}

      <SectionLabel>Maestro reports (“PISOS 2”)</SectionLabel>
      {!reports && <Loading what="reports" />}
      {reports && reports.length === 0 && <p className="hint">No open reports.</p>}
      {reports?.map((r) => (
        <div key={r.id} className="card">
          <p><strong>{r.maestro}</strong> says <strong>{r.stories} {r.stories === 1 ? "story" : "stories"}</strong> · {when(r.created_at)}</p>
          <p className="hint">{r.service ?? "No job"}{r.address ? ` · ${r.address}` : ""}{r.message ? ` · texted “${r.message}”` : ""}</p>
          <p className="hint">House now: {floors(r.current)}{r.current_source ? ` · ${STORIES_SOURCE_LABEL[r.current_source]}` : ""}</p>
          {!r.property_id && <Notice kind="info">No matching house for this job's address, so it can't be accepted. Reject it or fix the address.</Notice>}
          {r.current_source === "ops" || r.current_source === "owner" ? (
            <Notice kind="info">This house has a {STORIES_SOURCE_LABEL[r.current_source]} value; a Maestro report doesn't replace it. Set the house below if it's wrong.</Notice>
          ) : null}
          {allowed && (
            <div className="row-actions">
              {r.property_id && r.current_source !== "ops" && r.current_source !== "owner" && (
                <button type="button" className="btn" onClick={() => setPending({
                  title: `Accept: ${r.stories} ${r.stories === 1 ? "story" : "stories"}${r.address ? ` · ${r.address}` : ""}`,
                  change: { type: "property_fact", property_id: r.property_id!, stories: r.stories, report_id: r.id },
                })}>Accept</button>
              )}
              {rejecting === r.id ? (
                <>
                  <input className="search" placeholder="Why reject? (goes in the change log)" value={rejectWhy} onChange={(e) => setRejectWhy(e.target.value)} />
                  <button type="button" className="btn" disabled={rejectWhy.trim().length < 5} onClick={() => reject(r.id)}>Reject</button>
                  <button type="button" className="btn quiet" onClick={() => { setRejecting(null); setRejectWhy(""); }}>Cancel</button>
                </>
              ) : (
                <button type="button" className="btn quiet" onClick={() => setRejecting(r.id)}>Reject…</button>
              )}
            </div>
          )}
        </div>
      ))}

      <SectionLabel>Property Owner vs county (2+ floors apart)</SectionLabel>
      {!disagree && <Loading what="bookings" />}
      {disagree && disagree.length === 0 && <p className="hint">None. Every open booking agrees with the county within one floor.</p>}
      {disagree?.map((d) => (
        <div key={d.job_id} className="card">
          <p><strong>Owner says {d.owner_says}, county says {d.county_says}</strong> · {d.service} · {d.scheduled_date ?? "no date"} · <Chip tone={d.payment_status === "paid" ? "info" : "muted"}>{d.payment_status}</Chip></p>
          <p className="hint">{d.address ?? "—"} · job {d.job_id.slice(0, 8)}. Offers use the owner's answer. Check before dispatch; set the house below if needed.</p>
        </div>
      ))}

      <SectionLabel>Look up a house</SectionLabel>
      <div className="row-actions">
        <input className="search" placeholder="Street, e.g. 8018 206th St SE" value={street} onChange={(e) => setStreet(e.target.value)} />
        <input className="search" placeholder="ZIP" inputMode="numeric" maxLength={5} value={zip} onChange={(e) => setZip(e.target.value)} />
        <button type="button" className="btn" disabled={looking || !/^\d/.test(street.trim()) || !/^\d{5}$/.test(zip.trim())} onClick={lookup}>Look up</button>
      </div>
      {looking && <Loading what="the house" />}
      {found && !found.found && <Notice kind="info">No house found ({found.reason}). Check the street spelling and ZIP.</Notice>}
      {found?.found && p && (
        <div className="card">
          <p><strong>{p.address_normalized ?? "—"}</strong> · {p.gm_code}{found.match !== "exact" ? <> · <Chip tone="warn">close match, not exact</Chip></> : null}</p>
          <p>Stories: <strong>{floors(p.stories)}</strong>{p.stories_source ? ` · ${STORIES_SOURCE_LABEL[p.stories_source]}` : ""}{p.stories_updated_at ? ` · ${when(p.stories_updated_at)}` : ""}</p>
          <p className="hint">County records: {found.county ? `${floors(found.county.stories)} · “${found.county.bldg_type ?? "—"}” · ${found.county.file_year}${found.county.ambiguous ? " · houses on this parcel disagree" : ""}` : "no county row"}</p>
          <p className="hint">Lot {p.lot_sqft?.toLocaleString("en-US") ?? "—"} sq ft · home {p.home_sqft?.toLocaleString("en-US") ?? "—"} sq ft · built {p.year_built ?? "—"} · parcel {p.parcel_apn ?? "—"}</p>
          {(found.open_reports?.length ?? 0) > 0 && <Notice kind="info">{found.open_reports!.length} open Maestro report(s) for this house — see above.</Notice>}
          {allowed ? (
            <>
              <div className="segmented wrap" role="group" aria-label="Set stories">
                {CHOICES.map((n) => (
                  <button key={n} type="button" className={pick === n ? "seg on" : "seg"} aria-pressed={pick === n} onClick={() => setPick(n)}>{n}</button>
                ))}
              </div>
              <p className="hint">1.5 = 1½-story or split entry; texts round up (“2 pisos”).</p>
              <button type="button" className="btn" disabled={pick === null || pick === Number(p.stories)} onClick={() => setPending({
                title: `Set stories: ${pick} · ${p.address_normalized ?? p.gm_code}`,
                change: { type: "property_fact", property_id: p.id, stories: pick! },
              })}>Preview change</button>
            </>
          ) : <p className="hint">Viewers can look; pricing admins and owners can change.</p>}
        </div>
      )}
    </PageFrame>
  );
}
